import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "ui-components/data-table";
import Button, { ButtonSizes, ButtonThemes } from "../../ui-components/button/button";
import "./loadboardDispatchList.scss";
import ContentContainer from "templates/content-container/contentContainer";
import { PAGE_IDS } from "app/data/common/pageIds";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { Formik } from 'formik';
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import AssignDoorModal from "./assign-door";
import CloseDoorModal from "./CloseDoorModal/closeDoorModal";
import { useLoadboardManifestTrips } from "../../hooks/useLoadboard";
import { LoadboardListManifestRequestParams, LoadboardListManifestTrip, statusOptions, TIME_PERIOD_LIST } from "../../app/data/load-board/models";
import { useDestinationTerminals } from "hooks/usePickupSummary";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import ProgressBar from "../../ui-components/progress-bar/progressBar";
import { showPrettyTime } from "features/dock-loading/common";
import { useCarrierDropdown } from "hooks/useCarrier";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import LoadBoardService from "app/data/load-board/loadBoardService";
import { useQuery } from "@tanstack/react-query";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { userSelector } from "../../slices/user/userSlice";
import { AssignDoorFormValues } from "./assign-door/models";
import { ReactComponent as AssignDoor } from "../../images/assign_door_blue.svg";
import { ReactComponent as CloseDoor } from "../../images/close_door_blue.svg";
import { ReactComponent as Dispatch } from "../../images/dispatch _door_blue.svg";
import DispatchLoadModal from "./DispatchLoadModal/dispatchLoadModal";
import ManifestShipmentsSidebar, {
  ManifestShipmentsSidebarHandle,
} from "./manifest-shipments/manifestShipmentsSidebar";
import ServiceCentersState from "../../slices/service-centers/ServiceCentersState";
import { getServiceCenters, serviceCentersSelector } from "../../slices/service-centers/serviceCentersSlice";
import { UserUtils } from "app/data/user/userUtils";
import { FEATURE } from "app/route/RoutesConfig";
import StatusTag from "../../templates/content-container/statusTag";
import { initialTerminalSummaryState } from "slices/terminal-summary/TerminalSummaryState";
import { getTerminalsList, terminalSummarySelector } from "slices/terminal-summary/terminalSummarySlice";
import { useDataTableColumnPreferences } from "hooks/useDataTableColumnPreferences";
import { ReactComponent as PaperDocumentIcon } from "images/paper_document.svg";
import LoadBoardCarrierChangeLog from "./loadBoardCarrierChangeLog";
import DownloadButton from "ui-components/download-button/downloadButton";
import exportLoadBoardToCsv from "./exportLoadBoardToCsv";

const LOADBOARD_MANIFEST_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "manifestNumber", label: "Manifest" },
  { id: "doorNumber", label: "Door" },
  { id: "doorPriority", label: "!" },
  { id: "trips", label: "Trips" },
  { id: "parentOrigin", label: "Origin" },
  { id: "parentStop", label: "Stop" },
  { id: "parentDestination", label: "Destination" },
  { id: "laneStatus", label: "Lane Status" },
  { id: "nextStep", label: "Next Step" },
  { id: "loadedWeight", label: "Weight" },
  { id: "carrierNumber", label: "Carrier" },
  { id: "plannedCloseTime", label: "Plan Close Time" },
  { id: "dispatchTime", label: "Dispatch Time" },
  { id: "driver", label: "Driver" },
  { id: "trailerNumber", label: "Trailer" },
  { id: "tractorNumber", label: "Tractor" },
];

const LOADBOARD_SUB_TRIP_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "tripNumber", label: "Trip #" },
  { id: "doorNumber", label: "Door" },
  { id: "doorPriority", label: "!" },
  { id: "originTerminalName", label: "Origin" },
  { id: "destinationTerminalName", label: "Destination" },
  { id: "laneStatussab", label: "Lane Status" },
  { id: "loadedWeight", label: "Load %" },
  { id: "carrierName", label: "Carrier" },
  { id: "plannedCloseTime", label: "Plan Close Time" },
  { id: "dispatchTime", label: "Dispatch Time" },
  { id: "driver", label: "Driver" },
  { id: "trailerNumber", label: "Trailer" },
  { id: "tractorNumber", label: "Tractor" },
];

/** Stable non-empty filter/sort value for terminal columns (empty strings are auto-removed by equalsString). */
const getTerminalFilterValue = (
  airportCode?: string | null,
  name?: string | null,
  terminalNumber?: string | number | null
) => {
  const isPresent = (v: unknown) => {
    if (v == null) return false;
    const s = String(v).trim();
    return s !== "" && s.toLowerCase() !== "null";
  };
  const code = isPresent(airportCode) ? String(airportCode).trim() : "";
  const terminalName = isPresent(name) ? String(name).trim() : "";
  const number = isPresent(terminalNumber) ? String(terminalNumber).trim() : "";
  const label = [code, terminalName].filter(Boolean).join(" - ");
  if (label && number) return `${label} ${number}`;
  if (label) return label;
  return number;
};

const LoadboardDispatchList: React.FC<{}> = () => {

  const dispatch = useDispatch();
  const loadBoardService = LoadBoardService.getInstance();

  const manifestColumnPrefs = useDataTableColumnPreferences({
    instanceKey: "loadboard-dispatch-manifests",
    columnOptions: LOADBOARD_MANIFEST_COLUMN_OPTIONS,
  });

  const subTripColumnPrefs = useDataTableColumnPreferences({
    instanceKey: "loadboard-dispatch-sub-trips",
    columnOptions: LOADBOARD_SUB_TRIP_COLUMN_OPTIONS,
  });

  const [searchInput, setSearchInput] = useState("");
  const userState = useSelector(userSelector);
  const terminalSummaryState = useSelector(terminalSummarySelector);

  const terminalNumber = useMemo(() => {
    if (!userState.activeTerminal?.id) return 0;
    return userState.activeTerminal.id;
  }, [userState.activeTerminal?.id]);

  const [filterType, setFilterType] = useState<number>();
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterDoor, setFilterDoor] = useState("");
  const [filterOrigins, setFilterOrigins] = useState<string[]>(
    terminalNumber ? [terminalNumber.toString()] : []
  );
  const [filterDest, setFilterDest] = useState<number>();
  const [showAssignDoorModal, setShowAssignDoorModal] = useState<boolean>(false);
  const [showCarrierChangeLog, setShowCarrierChangeLog] = useState(false);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [visibleManifestRows, setVisibleManifestRows] = useState<LoadboardListManifestTrip[]>([]);
  const [timePeriod, setTimePeriod] = useState<string>("");

  const [terminalOptions, setTerminalOptions] = useState<{
    value: string;
    label: string;
  }[]>([]);

  useEffect(() => {
    if (terminalNumber) {
      setFilterOrigins(prev => {
        if (prev.length) return prev;
        return [terminalNumber.toString()];
      });
    }
  }, [terminalNumber]);

  const formatYYYYMMDDFilter = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  const getInitialDateRange = () => {
    const today = new Date();

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 3);

    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 3);

    return {
      start: formatYYYYMMDDFilter(startDate),
      end: formatYYYYMMDDFilter(endDate),
    };
  };

  const INITIAL_DATE_RANGE = getInitialDateRange();

  const isLinehaulManagementEnabled = !UserUtils.hasFeatureDisabled(userState.profile, FEATURE.LINEHAUL_MANAGEMENT);

  const [tempDateRange, setTempDateRange] = useState(INITIAL_DATE_RANGE);

  const [appliedFilters, setAppliedFilters] =
    useState<LoadboardListManifestRequestParams>({
      manifestOriginTerminalNumber: isLinehaulManagementEnabled ? undefined : terminalNumber,
      startCutDate: INITIAL_DATE_RANGE.start,
      endCutDate: INITIAL_DATE_RANGE.end,
    });

  const [assignDoorInitialValues, setAssignDoorInitialValues] = useState<Partial<AssignDoorFormValues> | undefined>(undefined);
  const [editingManifestNumber, setEditingManifestNumber] = useState<number | undefined>(undefined);
  const serviceCentersState: ServiceCentersState = useSelector(serviceCentersSelector);
  const [showCloseDoorModal, setShowCloseDoorModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchData, setDispatchData] = useState<any>(null);
  const [closedDoorData, setClosedDoorData] = useState<any>(null);
  const manifestShipmentsSidebarRef = useRef<ManifestShipmentsSidebarHandle>(null);
  const isAnyModalOpen = showAssignDoorModal || showCloseDoorModal || showDispatchModal;

  const { data, isLoading: isManifestLoading, error: manifestError, refetch } = useLoadboardManifestTrips(
    React.useMemo(() => ({ ...appliedFilters }), [appliedFilters])
  );

  const allTrips = React.useMemo(() => {
    return data?.flatMap((page) => page) || [];
  }, [data]);

  const { manifestData, tripsByManifest } = React.useMemo(() => {
    const filteredTrips = filterStatus.length > 0
  ? allTrips.filter((t) => filterStatus.includes(t.doorManifestStatus))
  : allTrips;

    const grouped = new Map<number, LoadboardListManifestTrip[]>();
    for (const trip of filteredTrips) {
      const list = grouped.get(trip.manifestNumber) ?? [];
      list.push(trip);
      grouped.set(trip.manifestNumber, list);
    }
    const manifestMap = new Map<number, LoadboardListManifestTrip>();
    const tripsByManifestMap = new Map<number, LoadboardListManifestTrip[]>();
    for (const [manifestNumber, trips] of grouped) {
      const sorted = [...trips].sort((a, b) => Number(a.tripNumber) - Number(b.tripNumber));
      tripsByManifestMap.set(manifestNumber, sorted);
      const activeTrip =
        sorted.find((t) => t.doorManifestStatus !== "DEPARTED") ?? sorted[sorted.length - 1];
      if (activeTrip) {
        manifestMap.set(manifestNumber, activeTrip);
      }
    }
    return {
      manifestData: Array.from(manifestMap.values()),
      tripsByManifest: tripsByManifestMap,
    };
  }, [allTrips, filterStatus]);

  const onVisibleManifestRowsChange = useCallback((rows: LoadboardListManifestTrip[]) => {
    setVisibleManifestRows(rows);
  }, []);

  const exportCSV = useCallback(() => {
    setLoadingCSV(true);
    try {
      // FE-only: export exact rows shown in the grid (main filters + column filters + sort).
      // Optional later: await loadBoardService.exportManifestTripsCsv(appliedFilters);
      const rows =
        visibleManifestRows.length > 0 ? visibleManifestRows : manifestData;
      exportLoadBoardToCsv(rows, tripsByManifest);
    } finally {
      setLoadingCSV(false);
    }
  }, [visibleManifestRows, manifestData, tripsByManifest]);

  const assignDoorDestinationTerminal = useMemo(() => {
    const val = assignDoorInitialValues?.destinationTerminalNumber;
    if (val == null) return undefined;
    const num = Number(val);
    return Number.isFinite(num) ? num : undefined;
  }, [assignDoorInitialValues?.destinationTerminalNumber]);



  const { data: doors, isLoading: isDoorsLoading } = useQuery({
    queryKey: [QUERY_KEYS.LOAD_BOARD_DOORS, terminalNumber],
    queryFn: async () => {
      if (!terminalNumber) return [];
      const response = await loadBoardService.getDoors(terminalNumber);
      return response.data || [];
    },
  });

  const doorOptions = useMemo(() => {
    return doors
      ?.filter(d => d.doorNumber)
      .map(d => ({
        label: d.doorNumber!,
        value: d.doorNumber!
      })) || [];
  }, [doors]);

  const {
    data: carrierDropdownData = [],
    isLoading: isCarrierLoading,
  } = useCarrierDropdown();

  const carrierOptions = useMemo(() => {
    return carrierDropdownData
      .filter(c => c?.carrierNumber && c?.carrierName)
      .map(c => ({
        value: c.carrierNumber,
        label: (
          <span>
            {c.carrierName}{" "}
            <span className="text-light">{c.carrierNumber}</span>
          </span>
        ),
        sortLabel: `${c.carrierName} ${c.carrierNumber}`,
      }))
      .sort((a, b) => a.sortLabel.localeCompare(b.sortLabel));
  }, [carrierDropdownData]);

  const { data: destinationterminalOptions = [], isLoading: destinationterminalsLoading } = useDestinationTerminals("");

  const getOption = (options: any[], value?: number | string) =>
    options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!serviceCentersState.loaded && !serviceCentersState.loading) dispatch(getServiceCenters());
  }, [dispatch, serviceCentersState]);

  const onSearchStart = useCallback(() => {
    let originPayload: number | number[] | string[] | undefined;

    if (isLinehaulManagementEnabled) {
      originPayload = isLinehaulManagementEnabled
        ? filterOrigins.map(Number)
        : terminalNumber
          ? [terminalNumber]
          : undefined;
    } else {
      originPayload = terminalNumber || undefined;
    }

    const newFilters: LoadboardListManifestRequestParams = {
      manifestOriginTerminalNumber: originPayload,
      manifestNumberSearch: searchInput.trim() || undefined,
      startCutDate: tempDateRange.start.toApiDateFormat() || undefined,
      endCutDate: tempDateRange.end.toApiDateFormat() || undefined,
      doorManifestStatus: filterStatus.length ? filterStatus : undefined,
      carrierNumber: filterType || undefined,
      doorNumber: filterDoor || undefined,
      manifestDestinationTerminalNumber: filterDest || undefined,
    };
    setAppliedFilters(newFilters);
  }, [isLinehaulManagementEnabled, terminalNumber, searchInput, tempDateRange, filterStatus, filterType, filterDoor, filterDest, filterOrigins]);

  const onSearchClear = (resetForm: any) => () => {
    setSearchInput("");
    setTempDateRange(INITIAL_DATE_RANGE);
    setFilterStatus([]);
    setFilterOrigins([terminalNumber.toString()]);
    setFilterType(undefined);
    setFilterDoor("");
    setFilterDest(undefined);
    resetForm({
      values: {
        doorManifestStatus: [],
        carrierNumber: "",
        doorNumber: "",
        manifestDestinationTerminalNumber: "",
      },
    });
    setAppliedFilters({
      manifestOriginTerminalNumber: terminalNumber,
      startCutDate: INITIAL_DATE_RANGE.start,
      endCutDate: INITIAL_DATE_RANGE.end,
    });
  };

  const formatYYYYMMDD = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  const getStartOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const getEndOfWeek = (date: Date) => {
    const start = getStartOfWeek(date);
    return new Date(start.setDate(start.getDate() + 6));
  };

  const handleTimePeriodChange = (option: any | null) => {
    if (!option) {
      setTimePeriod("");
      return;
    }

    setTimePeriod(option.value);

    const today = new Date();
    let startDate = new Date(today);
    let endDate = new Date(today);

    switch (option.value) {
      case "TODAY":
        break;

      case "YESTERDAY":
        startDate.setDate(today.getDate() - 1);
        endDate.setDate(today.getDate() - 1);
        break;

      case "TOMORROW":
        startDate.setDate(today.getDate() + 1);
        endDate.setDate(today.getDate() + 1);
        break;

      case "THIS_WEEK":
        startDate = getStartOfWeek(today);
        endDate = getEndOfWeek(today);
        break;

      case "LAST_WEEK":
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        startDate = getStartOfWeek(lastWeek);
        endDate = getEndOfWeek(lastWeek);
        break;

      case "NEXT_WEEK":
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        startDate = getStartOfWeek(nextWeek);
        endDate = getEndOfWeek(nextWeek);
        break;
    }

    setTempDateRange({
      start: formatYYYYMMDD(startDate),
      end: formatYYYYMMDD(endDate),
    });
  };

  const getTimePeriodListValues = () => {
    return Object.keys(TIME_PERIOD_LIST).map((key: string) => {
      return {
        value: key,
        label: TIME_PERIOD_LIST[key as keyof typeof TIME_PERIOD_LIST]
      };
    });
  };

  useEffect(() => {

    let originPayload: number | string[] | undefined;

    if (isLinehaulManagementEnabled) {
      originPayload = filterOrigins.length
        ? filterOrigins
        : terminalNumber || undefined;
    } else {
      originPayload = terminalNumber || undefined;
    }

    const newFilters: LoadboardListManifestRequestParams = {
      manifestOriginTerminalNumber: originPayload,
      manifestNumberSearch: searchInput.trim() || undefined,
      startCutDate: tempDateRange.start?.toApiDateFormat?.() || tempDateRange.start,
      endCutDate: tempDateRange.end?.toApiDateFormat?.() || tempDateRange.end,
      doorManifestStatus: filterStatus || undefined,
      carrierNumber: filterType || undefined,
      doorNumber: filterDoor || undefined,
      manifestDestinationTerminalNumber: filterDest || undefined,
    };
    setAppliedFilters(newFilters);
  }, [
    isLinehaulManagementEnabled,
    terminalNumber,
    filterOrigins,
    searchInput,
    tempDateRange.start,
    tempDateRange.end,
    filterStatus,
    filterType,
    filterDoor,
    filterDest,
  ]);

  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (initialTerminalSummaryState.terminallist) {
      const terminalOptions = terminalSummaryState.terminallist?.map((terminal) => ({
        value: terminal.terminalNumber.toString(),
        label: terminal.terminalName.toLowerCase().capitalize()
      })) || [];
      setTerminalOptions(terminalOptions);
    }
  }, [terminalSummaryState.terminallist]);

  const columns: ColumnDef<LoadboardListManifestTrip>[] = [
    {
      accessorKey: "manifestNumber",
      header: "Manifest",
      size: 140,
      filterFn: "includesString",
      cell: ({ row, getValue }) => {
        const canExpand = row.getCanExpand();
        const manifestNumber = getValue() as string | number | undefined;
        if (!manifestNumber) return '-';
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
            }}
          >
            <div
              style={{
                width: 16, // reserve space
                display: "flex",
                justifyContent: "center",
              }}
            >
              {canExpand && (
                <button
                  onClick={row.getToggleExpandedHandler()}
                  style={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      transform: row.getIsExpanded()
                        ? "rotate(0deg)"
                        : "rotate(-90deg)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  >
                    <path
                      d="M12 1.20856L10.7914 -1.28746e-05L6 4.79142L1.20857 -1.28746e-05L0 1.20856L6 7.20856L12 1.20856Z"
                      fill="#828282"
                    />
                  </svg>
                </button>
              )}
            </div>
            <button
              type="button"
              className="xgs-table-link-cell"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                font: "inherit",
              }}
              onClick={(e) => {
                e.stopPropagation();
                const trip = row.original;
                manifestShipmentsSidebarRef.current?.open({
                  manifestNumber,
                  originLabel: getTerminalFilterValue(
                    trip.originTerminalAirportCode,
                    trip.originTerminalName,
                    trip.originTerminalNumber
                  ) || undefined,
                  destinationLabel: getTerminalFilterValue(
                    trip.destinationTerminalAirportCode,
                    trip.destinationTerminalName,
                    trip.destinationTerminalNumber
                  ) || undefined,
                });
              }}
            >
              MF-{manifestNumber}
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "doorNumber",
      header: "Door",
      size: 110,
      meta: {
        filterVariant: "select",
        filterLabelFormatter: (key: string | number | undefined | null) =>
          key == null || key === "" ? "—" : `D-${key}`,
      },
      filterFn: "equalsString",
      cell: ({ row }) => {
        const doorNumber = row.original?.doorNumber;
        const status = row.original?.doorManifestStatus;

        if (doorNumber) {
          return <span>D-{doorNumber}</span>;
        }

        if (!doorNumber && status === "UNASSIGNED") {
          return (
            <span
              onClick={() => {
                setAssignDoorInitialValues(row.original);
                setEditingManifestNumber(Number(row.original.manifestNumber));
                setShowAssignDoorModal(true);
              }}
              style={{
                color: "#2F80ED",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              <AssignDoor /> Assign Door
            </span>
          );
        }

        return <span>-</span>;
      },
    },
    {
      accessorKey: "doorPriority",
      header: "!",
      size: 60,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
    },
    {
      id: "trips",
      header: "Trips",
      size: 80,
      accessorFn: (row) => {
        const trips = tripsByManifest.get(row.manifestNumber) ?? [];
        if (!trips.length) return "";
        return String(Math.max(...trips.map((t) => Number(t.tripNumber))));
      },
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
      cell: ({ row }) => {
        const trips = tripsByManifest.get(row.original.manifestNumber) ?? [];
        if (!trips.length) return "-";

        const highestTrip = Math.max(...trips.map(t => Number(t.tripNumber)));
        return <span>{highestTrip}</span>;
      },
    },
    {
      id: "parentOrigin",
      header: "Origin",
      accessorFn: (row) => {
        if (!row) return "";
        const trips = tripsByManifest.get(row.manifestNumber);
        const first = trips?.[0];
        if (!first) return "";
        return getTerminalFilterValue(
          first.originTerminalAirportCode,
          first.originTerminalName,
          first.originTerminalNumber
        );
      },
      enableSorting: true,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
      cell: ({ row }) => {
        const trips = tripsByManifest.get(row.original.manifestNumber);
        const first = trips?.[0];
        if (!first) return "-";

        return (
          <span>
            {first.originTerminalAirportCode ?? ""} - {first.originTerminalName ?? ""}{" "}
            <span className="text-light">{first.originTerminalNumber}</span>
          </span>
        );
      },
      size: 150,
    },
    {
      id: "parentStop",
      header: "Stop",
      accessorFn: (row) => {
        if (!row) return "";
        const trips = tripsByManifest.get(row.manifestNumber);
        const middle = trips && trips.length >= 2 ? trips[1] : null;
        if (!middle) return "";
        return getTerminalFilterValue(
          middle.originTerminalAirportCode,
          middle.originTerminalName,
          middle.originTerminalNumber
        );
      },
      enableSorting: true,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
      cell: ({ row }) => {
        const trips = tripsByManifest.get(row.original.manifestNumber);
        const middle = trips && trips.length >= 2 ? trips[1] : null;
        if (!middle) return "-";

        return (
          <span>
            {middle.originTerminalAirportCode ?? ""} - {middle.originTerminalName ?? ""}{" "}
            <span className="text-light">{middle.originTerminalNumber}</span>
          </span>
        );
      },
      size: 150,
    },
    {
      id: "parentDestination",
      header: "Destination",
      accessorFn: (row) => {
        if (!row) return "";
        const trips = tripsByManifest.get(row.manifestNumber);
        const last = trips?.[trips.length - 1];
        if (!last) return "";
        return getTerminalFilterValue(
          last.destinationTerminalAirportCode,
          last.destinationTerminalName,
          last.destinationTerminalNumber
        );
      },
      enableSorting: true,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
      cell: ({ row }) => {
        const trips = tripsByManifest.get(row.original.manifestNumber);
        const last = trips?.[trips.length - 1];
        if (!last) return "-";

        return (
          <span>
            {last.destinationTerminalAirportCode ?? ""} - {last.destinationTerminalName ?? ""}{" "}
            <span className="text-light">{last.destinationTerminalNumber}</span>
          </span>
        );
      },
      size: 150,
    },
    {
      id: "laneStatus",
      accessorKey: "doorManifestStatus",
      header: "Lane Status",
      size: 120,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
      cell: ({ getValue }) => {
        const colorMap = {
          DEPARTED: TagColor.GREEN,
          LOADING: TagColor.BLUE,
          CLOSED: TagColor.ORANGE,
          UNASSIGNED: TagColor.GREY,
        };

        const value = getValue();

        return (
          <Tag mods={{ color: colorMap[value] ?? TagColor.GREY }}>
            {value}
          </Tag>
        );
      }
    },
    {
      id: "nextStep",
      accessorKey: "doorManifestStatus",
      header: "Next Step",
      size: 120,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const activeTrip = row.original;
        let status = activeTrip?.doorManifestStatus;


        switch (status) {
          case "UNASSIGNED":
            return (
              <span
                onClick={() => {
                  setAssignDoorInitialValues(activeTrip);
                  setEditingManifestNumber(Number(row.original.manifestNumber));
                  setShowAssignDoorModal(true);
                }}
                style={{
                  color: "#2F80ED",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                <AssignDoor /> Assign Door
              </span>
            );

          case "LOADING":
            return (
              <span
                onClick={() => {
                  setClosedDoorData(activeTrip);
                  setShowCloseDoorModal(true);
                }}
                style={{
                  color: "#2F80ED",
                  cursor: "pointer",
                  fontWeight: 500,
                }}>
                <CloseDoor /> Close Door
              </span>
            );

          case "CLOSED":
            return (
              <span
                onClick={() => {
                  setDispatchData(activeTrip);
                  setShowDispatchModal(true);
                }}
                style={{
                  color: "#2F80ED",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                <Dispatch /> Dispatch
              </span>
            );

          case "DEPARTED":
            return <span>-</span>;

          default:
            return <span>-</span>;
        }
      },
    },
    {
      accessorKey: "loadedWeight",
      header: "Weight",
      size: 110,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const loadedWeight = Number(getValue()) || 0;
        const MAX_CAPACITY = 42000;

        const loadPercentage = Math.min(
          Math.round((loadedWeight / MAX_CAPACITY) * 100),
          100
        );

        return (
          <ProgressBar
            percentage={loadPercentage}
            additionalInfo={showPrettyTime(`${loadPercentage}%`)}
            multicolor
          />
        );
      },
    },
    {
      id: "carrierNumber",
      accessorFn: (row) => row.carrierName ?? "",
      header: "Carrier",
      size: 150,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
      cell: ({ row }) => (
        <span>
          {row?.original?.carrierName} <span className="text-light">{row?.original?.carrierNumber}</span>
        </span>
      ),
      // cell: ({ row, column }) => {
      //   const isEditing =
      //     editingCell?.rowId === row.id &&
      //     editingCell?.columnId === column.id;

      //   if (isEditing) {
      //     return (
      //       <LabeledSelectInput
      //         className="xgs-loadboard-dispatch__carrier-edit-dropdown"
      //         options={carrierOptions}
      //         value={getOption(carrierOptions, Number(tempValue))}
      //       // onValueChange={(opt) => {
      //       //   const value = opt?.value ? String(opt.value) : "";
      //       //   setTempValue(value);
      //       //   saveEdit(row, column.id); // optional: auto-save on select
      //       // }}
      //       />
      //     );
      //   }

      //   return (
      //     <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      //       <span>
      //         {row.original.carrierName}{" "}
      //         <span className="text-light">{row.original.carrierNumber}</span>
      //       </span>

      //       <EditIcon
      //         width={12}
      //         height={12}
      //         style={{ cursor: "pointer", flexShrink: 0 }}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           startEdit(row, column.id, row.original.carrierNumber);
      //         }}
      //       />
      //     </div>
      //   );
      // },
    },
    {
      id: "plannedCloseTime",
      accessorFn: (row) =>
        [row.plannedCloseDate?.toUiDateFormat?.() ?? row.plannedCloseDate, row.plannedCloseTime?.toUiTimeFormat?.() ?? row.plannedCloseTime]
          .filter(Boolean)
          .join(" "),
      header: "Plan Close Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.plannedCloseDate?.toUiDateFormat()} <span className="text-light">{row?.original?.plannedCloseTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 170,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
    },
    {
      id: "dispatchTime",
      accessorFn: (row) =>
        [row.dispatchDate?.toUiDateFormat?.() ?? row.dispatchDate, row.dispatchTime?.toUiTimeFormat?.() ?? row.dispatchTime]
          .filter(Boolean)
          .join(" "),
      header: "Dispatch Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.dispatchDate?.toUiDateFormat()} <span className="text-light">{row?.original?.dispatchTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 140,
      meta: { filterVariant: "select" },
      filterFn: "equalsString",
    },
    {
      id: "driver",
      accessorFn: (row) => row.driverName ?? row.driver ?? "",
      header: "Driver",
      size: 130,
      filterFn: "includesString",
      cell: ({ row }) => (
        <a
          className="xgs-table-link-cell"
          href={`https://cloud.samsara.com/o/25236/fleet/drivers?q=${encodeURIComponent(row.original?.driverId || "")}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>
            {row.original?.driverName} <span className="text-light">{row.original?.driverId}</span>
          </span>
        </a>
      ),
    },
    {
      accessorKey: "trailerNumber",
      header: "Trailer",
      size: 100,
      meta: {
        filterVariant: "select",
        filterLabelFormatter: (key: string | number | undefined | null) =>
          key == null || key === "" ? "—" : `TRL - ${key}`,
      },
      filterFn: "equalsString",
      cell: ({ row }) => (
        <a
          href={`https://cloud.samsara.com/o/25236/fleet/asset?q=${encodeURIComponent(row.original?.trailerNumber || "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="xgs-table-link-cell"
        >
          <span>
            {row.original?.trailerNumber && "TRL - " + row.original?.trailerNumber}
          </span>
        </a>
      ),
    },
    {
      accessorKey: "tractorNumber",
      header: "Tractor",
      size: 100,
      meta: {
        filterVariant: "select",
        filterLabelFormatter: (key: string | number | undefined | null) =>
          key == null || key === "" ? "—" : `TRC - ${key}`,
      },
      filterFn: "equalsString",
      cell: ({ row }) => (
        <a
          className="xgs-table-link-cell"
          href={`https://cloud.samsara.com/o/25236/fleet/asset?q=${encodeURIComponent(row.original?.tractorNumber || "")}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>
            {row.original?.tractorNumber && "TRC - " + row.original?.tractorNumber}
          </span>
        </a>
      ),
      // cell: ({ row, column, getValue }) => {
      //   const value = getValue() as string;
      //   const isEditing =
      //     editingCell?.rowId === row.id &&
      //     editingCell?.columnId === column.id;

      //   if (isEditing) {
      //     return (
      //       <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      //         <LabeledSelectInput
      //           name="tractor"
      //           label="Tractor"
      //           labelMode={LabelModes.column}
      //           placeholder="Start Typing..."
      //           required
      //           value={tractorFormValue}
      //           options={[]}
      //           className="xgs-dispatch-load-modal__field"
      //           onValueChange={onChangeTractor => setTractorFormValue(onChangeTractor || null)}
      //           loadOptions={loadTrailerOptions}
      //           async
      //         />
      //       </div>
      //     );
      //   }

      //   return (
      //     <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      //       <span>{value ? `TRC - ${value}` : "-"}</span>
      //       <EditIcon
      //         width={12}
      //         height={12}
      //         style={{ cursor: "pointer", flexShrink: 0 }}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           startEdit(row, column.id, value);
      //         }}
      //       />
      //     </div>
      //   );
      // },
    },
  ];

  const subColumns: ColumnDef<LoadboardListManifestTrip>[] = [
    {
      accessorKey: "tripNumber",
      header: "Trip #",
      size: 60
    },
    {
      accessorKey: "doorNumber",
      header: "Door",
      size: 120,
      cell: ({ row }) => {
        const doorNumber = row.original?.doorNumber;
        const status = row.original?.doorManifestStatus;

        if (doorNumber) {
          return <span>D-{doorNumber}</span>;
        }

        if (!doorNumber && status === "UNASSIGNED") {
          return (
            <span
              onClick={() => {
                setAssignDoorInitialValues(row.original);
                setEditingManifestNumber(Number(row.original.manifestNumber));
                setShowAssignDoorModal(true);
              }}
              style={{
                color: "#2F80ED",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              <AssignDoor /> Assign Door
            </span>
          );
        }

        return <span>-</span>;
      },
    },
    {
      accessorKey: "doorPriority",
      header: "!",
      size: 70,
    },
    {
      accessorKey: "originTerminalName",
      header: "Origin",
      cell: ({ row }) => (
        <span>
          {row?.original?.originTerminalAirportCode} - {row?.original?.originTerminalName} <span className="text-light">{row?.original?.originTerminalNumber}</span>
        </span>
      ),
      size: 120,
    },
    {
      accessorKey: "destinationTerminalName",
      header: "Destination",
      cell: ({ row }) => (
        <span>
          {row?.original?.destinationTerminalAirportCode} - {row?.original?.destinationTerminalName} <span className="text-light">{row?.original?.destinationTerminalNumber}</span>
        </span>
      ),
      size: 120,
    },
    {
      id: "laneStatussab",
      accessorKey: "doorManifestStatus",
      header: "Lane Status",
      size: 110,
      cell: ({ getValue }) => {
        const colorMap = {
          DEPARTED: TagColor.GREEN,
          LOADING: TagColor.BLUE,
          CLOSED: TagColor.ORANGE,
          UNASSIGNED: TagColor.GREY,
        };

        const value = getValue();

        return (
          <Tag mods={{ color: colorMap[value] ?? TagColor.GREY }}>
            {value}
          </Tag>
        );
      }
    },
    {
      accessorKey: "loadedWeight",
      header: "Load %",
      size: 100,
      cell: ({ getValue }) => {
        const loadedWeight = Number(getValue()) || 0;
        const MAX_CAPACITY = 42000;

        const loadPercentage = Math.min(
          Math.round((loadedWeight / MAX_CAPACITY) * 100),
          100
        );

        return (
          <ProgressBar
            percentage={loadPercentage}
            additionalInfo={showPrettyTime(`${loadPercentage}%`)}
            multicolor
          />
        );
      },
    },
    {
      accessorKey: "carrierName",
      header: "Carrier",
      cell: ({ row }) => (
        <span>
          {row?.original?.carrierName} <span className="text-light">{row?.original?.carrierNumber}</span>
        </span>
      ),
      size: 140,
    },
    {
      accessorKey: "plannedCloseTime",
      header: "Plan Close Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.plannedCloseDate?.toUiDateFormat()} <span className="text-light">{row?.original?.plannedCloseTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 130,
    },
    {
      accessorKey: "dispatchTime",
      header: "Dispatch Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.dispatchDate?.toUiDateFormat()} <span className="text-light">{row?.original?.dispatchTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 120,
    },
    // {
    //   accessorKey: "pros",
    //   header: "PROs",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() as string}
    //     </span>,
    //   size: 70,
    // },
    // {
    //   accessorKey: "pieces",
    //   header: "Pieces",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() as string}
    //     </span>,
    //   size: 70,
    // },
    // {
    //   accessorKey: "weight",
    //   header: "Weight",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() && getValue() as string + " lbs"}
    //     </span>,
    //   size: 70,
    // },
    // {
    //   accessorKey: "eta",
    //   header: "ETA",
    //   cell: ({ row }) => {
    //     return (
    //       <span
    //         onClick={() => setShowMainfestFreightDetailsModal(true)}
    //         style={{ color: '#2F80ED', cursor: 'pointer' }}
    //       >
    //         {[
    //           row?.original?.eta?.toCustomDateFormat("MM/DD"),
    //           // row?.original?.dispatchDate.toCustomDateFormat("MM/DD")
    //         ]
    //           .filter(Boolean)
    //           .join(" - ")}
    //       </span>
    //     );
    //   },
    //   size: 70,
    // },
    {
      accessorKey: "driver",
      header: "Driver",
      cell: ({ row }) => (
        <a
          href={`https://cloud.samsara.com/o/25236/fleet/drivers?q=${encodeURIComponent(row.original?.driverId || "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="xgs-table-link-cell"
        >
          <span>
            {row.original?.driverName} <span className="text-light">{row.original?.driverId}</span>
          </span>
        </a>
      ),
      size: 100,
    },
    {
      accessorKey: "trailerNumber",
      header: "Trailer",
      cell: ({ row }) => (
        <a
          href={`https://cloud.samsara.com/o/25236/fleet/asset?q=${encodeURIComponent(row.original?.trailerNumber || "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="xgs-table-link-cell"
        >
          <span>
            {row.original?.trailerNumber && "TRL - " + row.original?.trailerNumber}
          </span>
        </a>
      ),
      size: 100,
    },
    {
      accessorKey: "tractorNumber",
      header: "Tractor",
      cell: ({ row }) => (
        <a
          className="xgs-table-link-cell"
          href={`https://cloud.samsara.com/o/25236/fleet/asset?q=${encodeURIComponent(row.original?.tractorNumber || "")}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>
            {row.original?.tractorNumber && "TRC - " + row.original?.tractorNumber}
          </span>
        </a>
      ),
      size: 100,
    },
  ];

  const renderSubComponent = (row: any) => {
    const parentManifestNumber = row.original.manifestNumber;
    const subTrips = tripsByManifest.get(parentManifestNumber) ?? [];

    if (subTrips.length <= 1) return null;

    return (
      <div style={{ padding: "10px 20px", background: "#0000000f" }}>
        <DataTable
          data={subTrips}
          columns={subColumns}
          responsive
          rowHeight={82}
          enableRowSelection={false}
          enableColumnReorder
          autoHeight={false}
          shrinkToContent
          {...subTripColumnPrefs}
          showColumnSelector
          columnSelectorOptions={LOADBOARD_SUB_TRIP_COLUMN_OPTIONS}
        />
      </div>
    );
  };
  return (
    <ContentContainer
      pageId={PAGE_IDS.LOADBOARD_DISPATCH}
      titleComponent={
        <div className="xgs-loadboard-dispatch__header">
          <ContentContainerToolbar
            title="Load Board & Dispatch"
            className="xgs-loadboard-dispatch__toolbar"
            mods={{ "full-width": true }}
            tooltip={"This module is in Preview. Certain data and features may be incomplete or under development."}
            status={"PREVIEW"}
            titleAddon={
              isLinehaulManagementEnabled ? (
                <div className="xgs-loadboard-dispatch__linehaul-tag">
                  <StatusTag
                    status="LINEHAUL ACCESS"
                    tooltip="Cross-terminal filters are enabled for Linehaul users. Standard users only see their assigned terminal."
                  />
                </div>
              ) : undefined
            }
          >
            <div className="xgs-loadboard-dispatch__header-actions">
              <DownloadButton
                title="Download CSV"
                spinner={loadingCSV}
                disabled={isManifestLoading}
                onClick={exportCSV}
              />
              <Button
                type="button"
                theme={ButtonThemes.transparent}
                size={ButtonSizes.small}
                className="xgs-loadboard-dispatch__carrier-log-btn"
                onClick={() => setShowCarrierChangeLog(true)}
              >
                <PaperDocumentIcon />
                Carrier Change Log
              </Button>
            </div>
          </ContentContainerToolbar>
        </div>
      }
    >
      <div className={`xgs-loadboard-dispatch ${isAnyModalOpen ? "xgs-loadboard-dispatch__backdisable-disabled" : ""}`}>
        <div className="xgs-loadboard-dispatch xgs-list__controls">
          <Formik
            enableReinitialize={false}
            initialValues={{
              doorManifestStatus: filterStatus,
              carrierNumber: filterType,
              doorNumber: filterDoor,
              manifestDestinationTerminalNumber: filterDest
            }}
            onSubmit={onSearchStart}
          >
            {({ getFieldProps, resetForm, values }) => (
              <div className="dispatch-filters">
                <div className="dispatch-filters__grid">
                  <LabeledTextInput
                    name="manifestNumberSearch"
                    labelMode={LabelModes.column}
                    className="xgs-loadboard-dispatch__filter-select"
                    label="Search Manifest:"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.currentTarget.value)}
                    onKeyDown={(e) => e.key === "Enter" && onSearchStart()}
                    placeholder="e.g. 100234"
                  />
                  <XGSFormSelect
                    name="timePeriod"
                    label="Time Period:"
                    labelMode={LabelModes.column}
                    placeholder="Select..."
                    options={getTimePeriodListValues()}
                    value={
                      getTimePeriodListValues().find(
                        (option) => option.value === timePeriod
                      ) || null
                    }
                    onValueChange={(option) =>
                      handleTimePeriodChange(option ?? null)
                    }
                    className="xgs-loadboard-dispatch__filter-select"
                  />

                  <LabeledDateRangeInput
                    label="Date Range:"
                    labelMode={LabelModes.column}
                    className="xgs-loadboard-dispatch__filter-select"
                    start={tempDateRange.start}
                    end={tempDateRange.end}
                    onStartChange={(val) => setTempDateRange(prev => ({ ...prev, start: val }))}
                    onEndChange={(val) => setTempDateRange(prev => ({ ...prev, end: val }))}
                  />

                  <XGSFormSelect
                    name="doorManifestStatus"
                    label="Lane Status:"
                    labelMode={LabelModes.column}
                    className="xgs-loadboard-dispatch__filter-select"
                    options={statusOptions}
                    placeholder="All Status"
                    isClearable
                    isMulti
                    controlled={true}
                    value={statusOptions.filter(option => filterStatus.includes(option.value))} // controlled value
                    onMultiValuesChange={(selected: any[]) => {
                      const values = selected?.map(item => item.value) ?? [];
                      setFilterStatus(values);
                      // Optionally submit form or trigger refresh here
                      // props.submitForm();
                    }}
                  />

                  <XGSFormSelect
                    name="carrierNumber"
                    label="Carrier:"
                    className="xgs-loadboard-dispatch__filter-select"
                    placeholder="All Carrier"
                    isLoading={isCarrierLoading}
                    options={carrierOptions}
                    value={getOption(carrierOptions, filterType)}
                    onValueChange={(opt) =>
                      setFilterType(opt?.value ? Number(opt.value) : undefined)
                    }
                    labelMode={LabelModes.column}
                    formikProps={{ getFieldProps }}
                  />

                  <XGSFormSelect
                    name="doorNumber"
                    label="Door:"
                    className="xgs-loadboard-dispatch__filter-select"
                    placeholder="All Door"
                    isLoading={isDoorsLoading}
                    options={doorOptions}
                    value={getOption(doorOptions, filterDoor)}
                    onValueChange={(opt) => setFilterDoor(opt?.value || "")}
                    labelMode={LabelModes.column}
                    formikProps={{ getFieldProps }}
                  />

                  {isLinehaulManagementEnabled && (
                    <XGSFormSelect
                      name="manifestOriginTerminalNumber"
                      label="Origin:"
                      className="xgs-loadboard-dispatch__filter-select"
                      labelMode={LabelModes.column}
                      options={terminalOptions}
                      isMulti={true}
                      isClearable={false}
                      placeholder="Select Origins"
                      value={terminalOptions?.filter(option =>
                        filterOrigins.includes(option.value) ||
                        option.value === terminalNumber.toString()
                      )}
                      onMultiValuesChange={(selected: any[]) => {
                        let selectedValues = selected?.map(item => item.value) ?? [];

                        const currentTerminalValue = terminalNumber.toString();

                        if (!selectedValues.includes(currentTerminalValue)) {
                          selectedValues = [currentTerminalValue, ...selectedValues];
                        }

                        setFilterOrigins(selectedValues);
                      }}
                    />
                  )}
                  <XGSFormSelect
                    name="manifestDestinationTerminalNumber"
                    label="Destination:"
                    className="xgs-loadboard-dispatch__filter-select"
                    placeholder="All Destination"
                    isLoading={destinationterminalsLoading}
                    options={destinationterminalOptions}
                    value={getOption(destinationterminalOptions, filterDest)}
                    onValueChange={(opt) => setFilterDest(opt?.value ? Number(opt.value) : undefined)}
                    labelMode={LabelModes.column}
                    formikProps={{ getFieldProps }}
                  />

                  <div className="dispatch-filters__actions">
                    <Button
                      className="xgs-list__controls__button"
                      theme={ButtonThemes.blue}
                      disabled={isManifestLoading}
                      onClick={onSearchStart}>
                      Search
                    </Button>

                    <Button
                      className="xgs-list__controls__button"
                      theme={ButtonThemes.gray}
                      onClick={onSearchClear(resetForm)}>
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Formik>
        </div>

        {manifestError && (
          <XGSErrorMessage>Error loading Loadboard manifests. Please try again.</XGSErrorMessage>
        )}

        {!manifestError && (
          <>
            <div className="xgs-loadboard-dispatch__orders table-demo">
              <DataTable
                data={manifestData || []}
                columns={columns}
                isLoading={isManifestLoading}
                renderSubComponent={renderSubComponent}
                getRowCanExpand={(row) =>
                  (tripsByManifest.get(row.original.manifestNumber)?.length ?? 0) > 1
                }
                responsive={true}
                rowHeight={82}
                enableRowSelection={false}
                noResultsText="There are no records available"
                sorting={true}
                minTableHeight={500}
                infiniteScroll={true}
                infiniteScrollHasNext={false}
                stickyHeaders
                filters
                enableColumnResize
                enableColumnReorder
                showColumnSelector
                columnSelectorOptions={LOADBOARD_MANIFEST_COLUMN_OPTIONS}
                onVisibleRowsChange={onVisibleManifestRowsChange}
                {...manifestColumnPrefs}
              />
            </div>
          </>
        )}
      </div>
      <AssignDoorModal
        manifestNumber={editingManifestNumber || 0}
        destinationTerminal={assignDoorDestinationTerminal ?? 0}
        show={showAssignDoorModal && assignDoorDestinationTerminal != null}
        onClose={() => {
          setShowAssignDoorModal(false);
          setAssignDoorInitialValues(undefined);
          setEditingManifestNumber(undefined);
        }}
        onSave={() => {
          setShowAssignDoorModal(false);
          setAssignDoorInitialValues(undefined);
          setEditingManifestNumber(undefined);
          refetch();
        }}
        initialValues={assignDoorInitialValues}
      />
      <CloseDoorModal
        show={showCloseDoorModal}
        closeddoordata={closedDoorData}
        onClose={() => {
          setShowCloseDoorModal(false);
          setClosedDoorData(null);
        }}
      />
      <DispatchLoadModal
        show={showDispatchModal}
        dispatchdata={dispatchData}
        onClose={() => {
          setShowDispatchModal(false);
          setDispatchData(null);
        }}
      />
      <LoadBoardCarrierChangeLog
        show={showCarrierChangeLog}
        onClose={() => setShowCarrierChangeLog(false)}
      />
      <ManifestShipmentsSidebar ref={manifestShipmentsSidebarRef} />
    </ContentContainer>
  );
};

export default LoadboardDispatchList;